import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Breadcrumbs from '../../components/Admin/Breadcrumbs';
import { Plus, Star, Search } from 'lucide-react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useNotifications } from '../../hooks/useNotifications';
import { useConfirm } from '../../contexts/ConfirmContext';
import { getPostTitle, getBlogCategoryLabelKey, BLOG_CATEGORY_VALUES } from '../../i18n/localizeContent';
import { ActionButton, Button, Pagination } from '../../components/UI';

interface Post {
  _id: string;
  title: string;
  content: string;
  excerpt: string;
  i18n?: Record<string, { title?: string; excerpt?: string; content?: string }>;
  slug: string;
  author: {
    name: string;
  };
  category: string;
  tags: string[];
  featuredImage: string;
  isPublished: boolean;
  isFeatured: boolean;
  views: number;
  likes: number;
  publishedAt: string;
  createdAt: string;
}

const AdminPosts: React.FC = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const notifications = useNotifications();
  const confirm = useConfirm();

  const categories = BLOG_CATEGORY_VALUES;

  const fetchPosts = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '10',
        ...(searchTerm && { search: searchTerm }),
        ...(selectedCategory && { category: selectedCategory }),
      });

      const response = await axios.get(`/posts?${params}`);
      setPosts(response.data.posts);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      console.error('Erro ao carregar posts:', error);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, selectedCategory, currentPage]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const handleDeletePost = async (postId: string) => {
    if (!(await confirm({ message: t('admin.postDeleteConfirm'), confirmText: t('common.delete'), variant: 'danger' }))) return;
    try {
      await axios.delete(`/posts/${postId}`);
      setPosts(posts.filter(post => post._id !== postId));
      notifications.success.postDeleted();
    } catch (error: any) {
      console.error('Erro ao deletar post:', error);
      notifications.error.postDelete(error.response?.data?.message || 'Erro ao deletar post');
    }
  };

  const handleTogglePublish = async (postId: string, isPublished: boolean) => {
    try {
      await axios.put(`/posts/${postId}`, {
        isPublished: !isPublished
      });
      
      setPosts(posts.map(post => 
        post._id === postId 
          ? { ...post, isPublished: !isPublished }
          : post
      ));
      
      notifications.success.postUpdated();
    } catch (error: any) {
      console.error('Erro ao alterar status do post:', error);
      notifications.error.postUpdate(error.response?.data?.message || 'Erro ao alterar status do post');
    }
  };

  const handleToggleFeatured = async (postId: string, isFeatured: boolean) => {
    try {
      await axios.put(`/posts/${postId}`, {
        isFeatured: !isFeatured
      });
      
      setPosts(posts.map(post => 
        post._id === postId 
          ? { ...post, isFeatured: !isFeatured }
          : post
      ));
      
      notifications.success.postUpdated();
    } catch (error: any) {
      console.error('Erro ao alterar destaque do post:', error);
      notifications.error.postUpdate(error.response?.data?.message || 'Erro ao alterar destaque do post');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: string } = {
      'imigração': 'bg-blue-100 text-blue-800',
      'documentos': 'bg-green-100 text-green-800',
      'vistos': 'bg-purple-100 text-purple-800',
      'cidadania': 'bg-yellow-100 text-yellow-800',
      'trabalho': 'bg-red-100 text-red-800',
      'estudos': 'bg-indigo-100 text-indigo-800',
      'geral': 'bg-gray-100 text-gray-800',
    };
    return colors[category] || colors['geral'];
  };

  // Ações do post: mesma marcação na tabela (desktop) e nos cards (mobile).
  const renderActions = (post: (typeof posts)[number]) => (
    <div className="row-actions md:justify-end">
      <ActionButton
        action={post.isPublished ? 'unpublish' : 'publish'}
        onClick={() => handleTogglePublish(post._id, post.isPublished)}
      />
      <ActionButton
        action={post.isFeatured ? 'unfeature' : 'feature'}
        onClick={() => handleToggleFeatured(post._id, post.isFeatured)}
      />
      <ActionButton
        action="edit"
        to={`/admin/posts/${post._id}/edit`}
      />
      <ActionButton
        action="delete"
        onClick={() => handleDeletePost(post._id)}
      />
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <div className="mb-4">
            <Breadcrumbs />
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{t('admin.posts')}</h1>
              <p className="text-gray-600">{t('admin.postsDesc')}</p>
            </div>
            <Button
              to="/admin/posts/new">
              <Plus className="w-5 h-5" />
              <span>{t('admin.postNew')}</span>
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow mb-6 p-6">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder={t('admin.postsSearchPh')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="field pl-10 pr-4"
                />
              </div>
            </div>

            {/* Category Filter */}
            <div className="lg:w-64">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="field field--select"
              >
                <option value="">{t('blog.all')}</option>
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {t(getBlogCategoryLabelKey(category))}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Posts Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">{t('common.loading')}</p>
            </div>
          ) : posts.length === 0 ? (
            <div className="p-8 text-center">
              <div className="text-gray-400 mb-4">
                <Search className="w-16 h-16 mx-auto" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('admin.postsEmpty')}</h3>
              <p className="text-gray-600 mb-4">
                {searchTerm || selectedCategory 
                  ? 'Tente ajustar seus filtros de busca.'
                  : 'Comece criando seu primeiro post.'
                }
              </p>
              <Button to="/admin/posts/new">
                Criar Primeiro Post
              </Button>
            </div>
          ) : (
            <>
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Post
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Categoria
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Autor
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Data
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Visualizações
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {posts.map((post) => (
                    <tr key={post._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-12 w-12">
                            {post.featuredImage ? (
                              <img
                                className="h-12 w-12 rounded-lg object-cover"
                                src={post.featuredImage}
                                alt={getPostTitle(post, lang)}
                              />
                            ) : (
                              <div className="h-12 w-12 bg-gray-200 rounded-lg flex items-center justify-center">
                                <span className="text-gray-400 text-xs">Sem imagem</span>
                              </div>
                            )}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900 line-clamp-1">
                              {getPostTitle(post, lang)}
                            </div>
                            <div className="text-sm text-gray-500 line-clamp-1">
                              {post.excerpt}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getCategoryColor(post.category)}`}>
                          {post.category}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            post.isPublished 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {post.isPublished ? t('admin.statusPublished') : t('admin.statusDraft')}
                          </span>
                          {post.isFeatured && (
                            <Star className="w-4 h-4 text-yellow-500" />
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {post.author?.name || 'Autor não definido'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(post.publishedAt || post.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {post.views}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {renderActions(post)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <ul className="md:hidden divide-y divide-gray-200">
              {posts.map((post) => (
                <li key={post._id} className="p-4 space-y-3">
                  <div className="flex items-start gap-3">
                    {post.featuredImage ? (
                      <img className="h-14 w-14 rounded-lg object-cover shrink-0" src={post.featuredImage} alt={getPostTitle(post, lang)} />
                    ) : (
                      <div className="h-14 w-14 bg-gray-200 rounded-lg shrink-0" />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 line-clamp-2">{getPostTitle(post, lang)}</p>
                      <div className="mt-1 flex flex-wrap items-center gap-1.5">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                          post.isPublished ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {post.isPublished ? t('admin.statusPublished') : t('admin.statusDraft')}
                        </span>
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getCategoryColor(post.category)}`}>
                          {post.category}
                        </span>
                        {post.isFeatured && <Star className="w-4 h-4 text-yellow-500 fill-current" />}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {formatDate(post.publishedAt || post.createdAt)} · {post.views} visualizações
                      </p>
                    </div>
                  </div>
                  {renderActions(post)}
                </li>
              ))}
            </ul>
            </>
          )}

          <Pagination currentPage={currentPage} totalPages={totalPages} onChange={setCurrentPage} />
        </div>
      </div>
    </div>
  );
};

export default AdminPosts;
